/**
 * Géométrie partagée du tapis de jeu : taille des cartes, zones du pli,
 * position de la main.
 *
 * `PlayerHand` et `TrickDisplay` consomment tous les deux ce module, ce qui
 * garantit que les cartes en main et les cartes posées ont exactement la même
 * taille. Chacun avait auparavant son propre calcul (le pli se réduisait selon
 * la hauteur disponible, la main selon la largeur), d'où un facteur 2 entre les
 * deux en paysage sur mobile.
 *
 * Repère : la border-box de `.game-table`. C'est aussi le bloc conteneur des
 * enfants positionnés en absolu (`.trick-display`, `.player-hand-area`), donc
 * le padding de `.game-table` ne décale pas ces coordonnées.
 */

export type HandLayoutMode = 'flat' | 'arc';

/** Ratio largeur / hauteur des visuels de cartes. */
export const CARD_ASPECT = 120 / 218;

/** En dessous de cette hauteur de tapis, on bascule en régime compact. */
const COMPACT_HEIGHT = 520;

/** Padding horizontal de `.player-hand-area`, sur chaque côté. */
const HAND_AREA_PADDING_X = 20;
const HAND_AREA_PADDING_X_PORTRAIT = 6;

/** Marge conservée aux extrémités de l'arc, pour ne pas toucher les bords. */
const ARC_SIDE_MARGIN = 24;
const ARC_SIDE_MARGIN_PORTRAIT = 8;

function isPortraitBox(width: number, height: number): boolean {
  return height > width;
}

interface Regime {
  /** Recouvrement vertical Nord/Sud, en fraction de la hauteur de carte. */
  trickOverlap: number;
  /** Écart vertical Nord/Sud lorsqu'il n'y a pas de recouvrement (px). */
  trickRowGap: number;
  /**
   * Décalage horizontal Nord/Sud, en fraction de la largeur de carte (Nord
   * vers la gauche, Sud vers la droite).
   *
   * Indispensable dès qu'il y a recouvrement : le z-index suit l'ordre de pose,
   * donc sans décalage, une carte posée en second peut masquer complètement le
   * coin identifiant de la première.
   */
  trickLateralShift: number;
  /** Fraction de la hauteur de carte réellement visible dans la bande main. */
  handVisibleRatio: number;
  /** Provision initiale pour la flèche de l'arc, en fraction de la hauteur. */
  arcSagittaAllowance: number;
  topMargin: number;
  bottomMargin: number;
  /** Hauteur réservée aux cartouches joueurs au-dessus de la carte Nord. */
  labelBand: number;
  labelStyle: 'full' | 'chip';
  /** Largeur minimale réservée sur les côtés pour les cartouches Est/Ouest. */
  minSideMargin: number;
}

const ROOMY: Regime = {
  trickOverlap: 0,
  trickRowGap: 15,
  trickLateralShift: 0,
  handVisibleRatio: 0.75,
  arcSagittaAllowance: 0.16,
  topMargin: 10,
  bottomMargin: 10,
  labelBand: 65,
  labelStyle: 'full',
  minSideMargin: 100,
};

const COMPACT: Regime = {
  trickOverlap: 0.42,
  trickRowGap: 0,
  trickLateralShift: 0.16,
  handVisibleRatio: 0.40,
  arcSagittaAllowance: 0.12,
  topMargin: 6,
  bottomMargin: 8,
  labelBand: 22,
  labelStyle: 'chip',
  minSideMargin: 46,
};

/**
 * Portrait (iPhone / iPad) : chips, main en bas. Nord et Sud ne se
 * recouvrent que si le tapis est trop bas ; sinon la hauteur sert
 * à les écarter (Sud collé à la main, Nord vers le haut du tapis).
 */
const PORTRAIT: Regime = {
  trickOverlap: 0.42,
  trickRowGap: 16,
  trickLateralShift: 0.16,
  handVisibleRatio: 0.42,
  arcSagittaAllowance: 0.14,
  topMargin: 8,
  bottomMargin: 8,
  labelBand: 22,
  labelStyle: 'chip',
  minSideMargin: 46,
};

/** Largeur max d'une carte en portrait, en fraction du tapis. */
const PORTRAIT_CARD_WIDTH_FRAC = 0.24;

/** Recouvrement de la carte Sud par la bande de la main (portrait). */
const PORTRAIT_HAND_SUD_TUCK = 0.12;

// ============================================
// Arc de cercle
// ============================================

export interface ArcPosition {
  /** Position de repos, identique pour toutes les cartes. */
  left: number;
  top: number;
  /** Rotation autour du pivot commun, en degrés. */
  rotation: number;
  /** Ordonnée du pivot, relative au haut de la carte. */
  pivotY: number;
}

export interface ArcGeometry {
  positions: ArcPosition[];
  /** Flèche de l'arc : dénivelé entre la carte centrale et les extrémités. */
  sagitta: number;
  /** Pas angulaire entre deux cartes voisines, en degrés. */
  spacingDeg: number;
  /** Angle de la première carte, en degrés. */
  startAngleDeg: number;
  radius: number;
  pivotX: number;
  pivotY: number;
}

const EMPTY_ARC: ArcGeometry = {
  positions: [],
  sagitta: 0,
  spacingDeg: 0,
  startAngleDeg: 0,
  radius: 0,
  pivotX: 0,
  pivotY: 0,
};

/**
 * Toutes les cartes partagent le même pivot, situé loin sous la main. Seule la
 * rotation autour de ce pivot les distribue, ce qui donne un arc parfait sans
 * effet d'escalier.
 */
export function computeArcGeometry(
  cardCount: number,
  containerWidth: number,
  containerHeight: number,
  cardWidth: number,
  cardHeight: number,
  sideMargin?: number,
  portraitHint?: boolean,
): ArcGeometry {
  if (cardCount <= 0 || cardWidth <= 0 || cardHeight <= 0) return EMPTY_ARC;

  const left = containerWidth / 2 - cardWidth / 2;
  const portrait = portraitHint ?? isPortraitBox(containerWidth, containerHeight);
  const arcMargin = sideMargin ?? (
    portrait ? ARC_SIDE_MARGIN_PORTRAIT : ARC_SIDE_MARGIN
  );

  if (cardCount === 1) {
    return {
      ...EMPTY_ARC,
      positions: [{ left, top: 0, rotation: 0, pivotY: cardHeight / 2 }],
      pivotX: containerWidth / 2,
      pivotY: cardHeight / 2,
    };
  }

  const aspectRatio = containerWidth / Math.max(1, containerHeight);
  // Paysage : arc plat. Portrait : on ouvre davantage pour occuper la largeur.
  const maxArcDeg = portrait
    ? Math.min(68, Math.max(40, 90 / Math.max(aspectRatio, 0.35)))
    : Math.min(55, Math.max(28, 70 / aspectRatio));
  let spacingDeg = Math.max(2, Math.min(5, maxArcDeg / (cardCount - 1)));
  let totalAngleDeg = spacingDeg * (cardCount - 1);
  let halfAngleRad = ((totalAngleDeg / 2) * Math.PI) / 180;

  // Demi-largeur restante une fois retirées l'emprise de la carte de bout
  // (tournée) et la marge latérale.
  const availableHalf =
    containerWidth / 2 -
    arcMargin -
    (cardWidth / 2) * Math.cos(halfAngleRad) -
    (cardHeight / 2) * Math.sin(halfAngleRad);

  const maxRadius = containerWidth * 2;
  const fitRadius =
    halfAngleRad > 0.01 && availableHalf > 0
      ? availableHalf / Math.sin(halfAngleRad)
      : maxRadius;
  const radius = Math.min(maxRadius, fitRadius);

  // Peu de cartes : ne pas étaler jusqu'aux bords. Les centres restent assez
  // proches pour que les cartes se recouvrent (pas de tapis visible entre elles).
  const maxCenterGap = cardWidth * 0.62;
  const maxSpacingForOverlap = radius > 1 ? (maxCenterGap / radius) * (180 / Math.PI) : spacingDeg;
  if (spacingDeg > maxSpacingForOverlap) {
    spacingDeg = Math.max(1.2, maxSpacingForOverlap);
    totalAngleDeg = spacingDeg * (cardCount - 1);
    halfAngleRad = ((totalAngleDeg / 2) * Math.PI) / 180;
  }

  const pivotY = cardHeight / 2 + radius;
  const startAngleDeg = -totalAngleDeg / 2;

  const positions: ArcPosition[] = [];
  for (let i = 0; i < cardCount; i++) {
    positions.push({
      left,
      top: 0,
      rotation: startAngleDeg + i * spacingDeg,
      pivotY,
    });
  }

  return {
    positions,
    sagitta: radius * (1 - Math.cos(halfAngleRad)),
    spacingDeg,
    startAngleDeg,
    radius,
    pivotX: containerWidth / 2,
    pivotY,
  };
}

// ============================================
// Layout global du tapis
// ============================================

export interface TableLayoutInput {
  /** Largeur de la border-box de `.game-table`. */
  containerWidth: number;
  /** Hauteur de la border-box de `.game-table`. */
  containerHeight: number;
  /** Nombre de cartes en main (sert au dimensionnement de l'arc). */
  handCount: number;
  handLayout: HandLayoutMode;
  /** Plafond de taille, issu des variables CSS responsives. */
  maxCardHeight: number;
  /**
   * Orientation de l'appareil. Si absente, on retombe sur le ratio du
   * tapis — à éviter : une fois le puits dimensionné, le tapis est
   * presque carré et basculerait de régime.
   */
  orientation?: 'portrait' | 'landscape';
}

export interface TableLayout {
  compact: boolean;
  cardWidth: number;
  cardHeight: number;
  /** Recouvrement Nord/Sud appliqué, en fraction de la hauteur de carte. */
  trickOverlap: number;
  /** Décalage horizontal Nord/Sud, en px. */
  trickLateralShift: number;
  northZoneY: number;
  southZoneY: number;
  middleZoneY: number;
  sideMargin: number;
  labelBand: number;
  labelStyle: 'full' | 'chip';
  /** Ordonnée du haut de la bande de la main, publiée en `--hand-top`. */
  handTop: number;
  handBandHeight: number;
  /** Largeur utile pour la main, padding de `.player-hand-area` déduit. */
  handAvailableWidth: number;
  /** Padding horizontal de `.player-hand-area`, publié en `--hand-padding-x`. */
  handPaddingX: number;
  containerWidth: number;
  containerHeight: number;
  /**
   * Hauteur du puits de jeu (pli + main). En portrait, le tapis doit
   * s'aligner sur cette valeur pour laisser le haut au dock.
   */
  playWellHeight: number;
  /** Vrai quand le bloc de jeu est calé en bas et le tapis doit se dimensionner sur le puits. */
  anchorPlayWell: boolean;
  /** Orientation appareil utilisée pour le régime (pas le ratio du tapis). */
  portrait: boolean;
}

function packPlayWell(
  regime: Regime,
  cardHeight: number,
  sagitta: number,
  rowGap: number,
  handSudTuck: number,
): { handBandHeight: number; playWellHeight: number } {
  const handBandHeight = cardHeight * regime.handVisibleRatio + sagitta;
  const trickSpan = regime.labelBand + cardHeight * (2 - regime.trickOverlap) + rowGap;
  const playWellHeight =
    regime.topMargin + trickSpan + regime.bottomMargin + handBandHeight - handSudTuck;
  return { handBandHeight, playWellHeight };
}

export function computeTableLayout(input: TableLayoutInput): TableLayout {
  const { containerWidth, containerHeight, handCount, handLayout, maxCardHeight, orientation } = input;

  const portrait = orientation
    ? orientation === 'portrait'
    : isPortraitBox(containerWidth, containerHeight);
  const regime = portrait ? PORTRAIT : (containerHeight < COMPACT_HEIGHT ? COMPACT : ROOMY);
  const handPaddingX = portrait ? HAND_AREA_PADDING_X_PORTRAIT : HAND_AREA_PADDING_X;
  const handAvailableWidth = Math.max(120, containerWidth - handPaddingX * 2);

  const rowGap = regime.trickOverlap > 0 ? 0 : regime.trickRowGap;
  const fixedHeight = regime.topMargin + regime.bottomMargin + regime.labelBand + rowGap;
  const usableHeight = Math.max(120, containerHeight - fixedHeight);

  /**
   * Le tapis se lit comme un empilement d'unités « hauteur de carte » :
   * deux rangées de pli (moins le recouvrement), la part visible de la main,
   * et la flèche de l'arc.
   */
  const solveCardHeight = (sagittaRatio: number) => {
    const units = 2 - regime.trickOverlap + regime.handVisibleRatio + sagittaRatio;
    let height = Math.min(maxCardHeight, usableHeight / units);
    if (portrait) {
      height = Math.min(height, (containerWidth * PORTRAIT_CARD_WIDTH_FRAC) / CARD_ASPECT);
    }
    return height;
  };

  const probeArc = (height: number) => {
    const probeCount = Math.max(handCount, 18);
    return computeArcGeometry(
      probeCount,
      handAvailableWidth,
      containerHeight,
      height * CARD_ASPECT,
      height,
      undefined,
      portrait,
    );
  };

  // Portrait : la taille vient d'abord de la largeur (main pleine largeur).
  // Paysage : on remplit la hauteur disponible.
  let cardHeight = portrait
    ? Math.min(maxCardHeight, (containerWidth * PORTRAIT_CARD_WIDTH_FRAC) / CARD_ASPECT)
    : solveCardHeight(handLayout === 'arc' ? regime.arcSagittaAllowance : 0);
  let sagitta = 0;

  if (handLayout === 'arc' && cardHeight > 0) {
    // On dimensionne sur une main pleine (18) pour que la taille des cartes
    // ne grandisse pas à mesure que le pli avance. L'arc rendu, lui, utilise
    // le nombre réel de cartes.
    const probe = probeArc(cardHeight);
    if (portrait) {
      sagitta = probe.sagitta;
    } else {
      cardHeight = solveCardHeight(probe.sagitta / cardHeight);
      sagitta = probeArc(cardHeight).sagitta;
    }
  }

  const handSudTuck = portrait ? cardHeight * PORTRAIT_HAND_SUD_TUCK : 0;
  let packed = packPlayWell(regime, cardHeight, sagitta, rowGap, handSudTuck);

  // Tapis trop bas (dock + nav) : on réduit les cartes pour tenir.
  if (portrait && packed.playWellHeight > containerHeight + 1 && containerHeight > 120) {
    cardHeight = solveCardHeight(
      handLayout === 'arc' && cardHeight > 0 ? sagitta / cardHeight : 0,
    );
    if (handLayout === 'arc' && cardHeight > 0) {
      sagitta = probeArc(cardHeight).sagitta;
    }
    packed = packPlayWell(
      regime,
      cardHeight,
      sagitta,
      rowGap,
      cardHeight * PORTRAIT_HAND_SUD_TUCK,
    );
  }

  const cardWidth = cardHeight * CARD_ASPECT;
  const { handBandHeight, playWellHeight } = packed;

  // Main collée au bas du tapis : elle peut mordre sur la carte Sud
  // (z-index plus élevé).
  const handTop = containerHeight - regime.bottomMargin - handBandHeight;

  let northZoneY: number;
  let southZoneY: number;
  let appliedOverlap = regime.trickOverlap;
  let appliedShift = cardWidth * regime.trickLateralShift;

  if (portrait) {
    const southFromHand = handTop + cardHeight * PORTRAIT_HAND_SUD_TUCK - cardHeight;
    const northFromTop = regime.topMargin + regime.labelBand;
    const minGap = regime.trickRowGap;
    const canSpread = southFromHand >= northFromTop + cardHeight + minGap;
    if (canSpread) {
      northZoneY = northFromTop;
      southZoneY = southFromHand;
      appliedOverlap = 0;
      appliedShift = 0;
    } else {
      southZoneY = southFromHand;
      northZoneY = southZoneY - cardHeight * (1 - regime.trickOverlap);
    }
  } else {
    northZoneY = regime.topMargin + regime.labelBand;
    southZoneY = northZoneY + cardHeight * (1 - regime.trickOverlap) + rowGap;
  }
  const middleZoneY = (northZoneY + southZoneY) / 2;

  return {
    compact: regime !== ROOMY,
    cardWidth,
    cardHeight,
    trickOverlap: appliedOverlap,
    trickLateralShift: appliedShift,
    northZoneY,
    southZoneY,
    middleZoneY,
    sideMargin: Math.max(regime.minSideMargin, containerWidth * 0.08),
    labelBand: regime.labelBand,
    labelStyle: regime.labelStyle,
    handTop,
    handBandHeight,
    handAvailableWidth,
    handPaddingX,
    containerWidth,
    containerHeight,
    playWellHeight: portrait ? playWellHeight : containerHeight,
    anchorPlayWell: portrait,
    portrait,
  };
}

// ============================================
// Assise naturelle des cartes posées
// ============================================

/** Pseudo-aléatoire déterministe dans [0, 1). */
export function seededRand(seed: number): number {
  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}

export type TrickScreenPos =
  | 'south'
  | 'north'
  | 'east'
  | 'west'
  | 'north-east'
  | 'north-west'
  | 'center';

/** Orientation 90 / 180 / 45 selon le siège — sans jitter. */
export function trickOrientation(screenPos: TrickScreenPos): number {
  switch (screenPos) {
    case 'east':
      return 90;
    case 'west':
      return -90;
    case 'north-east':
      return 45;
    case 'north-west':
      return -45;
    case 'north':
      return 180;
    default:
      return 0;
  }
}

export interface TrickCardScatter {
  /** Inclinaison naturelle, ±5° environ. */
  rot: number;
  dx: number;
  dy: number;
}

/**
 * Léger désordre d'une carte posée, seedé par son numéro (stable d'un
 * render à l'autre). Le décalage Y reste petit : Nord/Sud se recouvrent.
 */
export function trickCardScatter(card: number): TrickCardScatter {
  return {
    rot: (seededRand(card + 500) - 0.5) * 10,
    dx: (seededRand(card + 700) - 0.5) * 12,
    dy: (seededRand(card + 900) - 0.5) * 6,
  };
}
